import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismIcon,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
// @ts-expect-error: Incorrect type
import stopPropagation from 'ember-event-helpers/helpers/stop-propagation';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import {
  CURRENCY_MAPPING,
  type SupportedCurrencies,
} from 'frontend-utils/utils/constants/currency-mapping-constants';
import GeneralCurrencyPickerGroup from 'lumen/components/general/currency-picker/group';
import GeneralCurrencyPickerItem from 'lumen/components/general/currency-picker/item';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownBasic from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-basic';
import HotelAndMarketCurrencyProfileCurrenciesModal from 'lumen/components/hotel-and-market/currency/profile-currencies-modal';
import type {
  Currency,
  CurrencyConversionExchangeRatesProfile,
  FavouriteCurrency,
} from 'lumen/models';
import { timeout } from 'lumen/utils/waiters';

import styles from './profile-currency-picker.module.css';

type CurrencyOption = {
  currency: Currency;
  label: string;
  value: SupportedCurrencies;
};

interface HotelAndMarketCurrencyProfileCurrencyPickerSignature {
  Args: {
    favouriteCurrencies: Currency[];
    onCurrencyChange?: (currencyCode: SupportedCurrencies | null) => unknown;
    selectedCurrencyCode: SupportedCurrencies | null;
    selectedProfile: CurrencyConversionExchangeRatesProfile | null;
  };
}

export default class HotelAndMarketCurrencyProfileCurrencyPicker extends Component<HotelAndMarketCurrencyProfileCurrencyPickerSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isProfileCurrenciesModalOpen = false;
  @tracked searchValue = '';

  addFavouriteTask = task(
    { drop: true },
    async (currency: Currency): Promise<void> => {
      const favouriteCurrency = this.store.createRecord<FavouriteCurrency>(
        'favourite-currency',
        {
          currency,
        },
      );
      await favouriteCurrency.save().catch((error) => {
        favouriteCurrency.rollbackAttributes();

        this.errorTracking.captureException(error);
        this.toast.error(
          this.intl.t('general.favourite_currency.add_failed', {
            currency: currency.code,
          }),
          { showIcon: true },
        );
      });

      this.usageAnalytics.track('Currency Favorited', {
        eventTrigger: 'currency-favourited',
        view: this.trackingViewLevel,
        code: currency.code,
      });
    },
  );

  onSearchInputTask = task(
    { restartable: true },
    async (value: string): Promise<void> => {
      this.searchValue = value;

      if (value) {
        await timeout();

        this.usageAnalytics.track('Exchange Rate Currency Searched', {
          eventTrigger: 'currency-searched',
          view: this.trackingViewLevel,
        });
      }
    },
  );

  removeFavouriteTask = task(
    { drop: true },
    async (currency: Currency): Promise<void> => {
      if (!currency.favourite) {
        return;
      }

      try {
        await currency.favourite.destroyRecord();
      } catch (error) {
        this.errorTracking.captureException(error);
        this.toast.error(
          this.intl.t('general.favourite_currency.remove_failed', {
            currency: currency.code,
          }),
          {
            showIcon: true,
          },
        );
      }

      this.usageAnalytics.track('Currency Unfavorited', {
        eventTrigger: 'currency-unfavourited',
        view: this.trackingViewLevel,
        code: currency.code,
      });
    },
  );

  toggleFavouriteTask = task(
    { drop: true },
    async (currency: Currency): Promise<void> => {
      if (currency.favourite) {
        await this.removeFavouriteTask.perform(currency);
      } else {
        await this.addFavouriteTask.perform(currency);
      }
    },
  );

  get baseCurrency(): Currency | null {
    return this.args.selectedProfile?.baseCurrency ?? null;
  }

  get favouriteOptions(): CurrencyOption[] {
    const favouriteCodes = new Set(
      this.args.favouriteCurrencies.map((c) => c.code),
    );
    return this.options.filter((option) => favouriteCodes.has(option.value));
  }

  get filteredOptions(): CurrencyOption[] {
    const search = this.searchValue.toLowerCase();
    if (!search) {
      return this.options;
    }
    return this.options.filter(
      (option) =>
        option.value.toLowerCase().includes(search) ||
        option.label.toLowerCase().includes(search),
    );
  }

  get isResetDisabled(): boolean {
    return (
      !this.baseCurrency ||
      this.args.selectedCurrencyCode === this.baseCurrency.code
    );
  }

  get options(): CurrencyOption[] {
    const profile = this.args.selectedProfile;
    const baseCurrency = this.baseCurrency;

    if (!profile || !baseCurrency) {
      return [];
    }

    const allOptions: CurrencyOption[] = [
      {
        currency: baseCurrency,
        label: `${baseCurrency.name} (${baseCurrency.symbol})`,
        value: baseCurrency.code,
      },
    ];

    profile.rates
      .filter(
        (rate) => rate.currency && rate.currency.code !== baseCurrency.code,
      )
      .forEach((rate) => {
        if (!rate.currency) {
          this.errorTracking.captureMessage(
            `Missing currency for rate with code ${rate.currencyCode} in profile ${profile.id}`,
          );
          return;
        }

        allOptions.push({
          currency: rate.currency,
          label: `${rate.currency.name} (${rate.currency.symbol})`,
          value: rate.currency.code,
        });
      });

    return allOptions;
  }

  get trackingViewLevel(): string {
    return this.currencyConversion.isHotelLevel
      ? 'hotel-level'
      : 'organization-level';
  }

  get triggerLabel(): string {
    const currencyCode = this.args.selectedCurrencyCode as SupportedCurrencies;
    const currencySymbol = CURRENCY_MAPPING[currencyCode];

    if (!currencySymbol) {
      this.errorTracking.captureMessage(
        `Currency symbol not found for ${currencyCode}`,
      );
      return `${currencyCode} (${currencyCode})`;
    }

    return `${currencyCode} (${currencySymbol})`;
  }

  @action
  closeProfileCurrenciesModal(): void {
    this.isProfileCurrenciesModalOpen = false;
  }

  @action
  onClose(): void {
    this.searchValue = '';
  }

  @action
  openProfileCurrenciesModal(dropdown: { toggleDropdown: () => void }): void {
    this.onClose();
    dropdown.toggleDropdown();
    this.isProfileCurrenciesModalOpen = true;

    this.usageAnalytics.track('Portfolio Currencies Viewed', {
      eventTrigger: 'portfolio-currency-viewed',
      view: this.trackingViewLevel,
    });
  }

  @action
  resetCurrency(dropdown: { toggleDropdown: () => void }): void {
    const baseCurrencyCode = this.baseCurrency?.code;
    if (!baseCurrencyCode) {
      return;
    }
    this.onClose();
    dropdown.toggleDropdown();
    this.args.onCurrencyChange?.(baseCurrencyCode);

    this.usageAnalytics.track('Default Currency Selected', {
      eventTrigger: 'local-currency-resetted',
      view: this.trackingViewLevel,
    });
  }

  @action
  selectCurrency(option: CurrencyOption): void {
    this.args.onCurrencyChange?.(option.value);

    const view = this.trackingViewLevel;
    const isFavourite = this.favouriteOptions.some(
      (fav) => fav.value === option.value,
    );

    this.usageAnalytics.track(
      isFavourite
        ? 'Exchange Rate Currency Favourite Selected'
        : 'Exchange Rate Currency Selected',
      {
        eventTrigger: isFavourite
          ? 'currency-favourite-selected'
          : 'currency-selected',
        view,
        code: option.value,
      },
    );
  }

  <template>
    <div class="relative">
      <GeneralDropdownsDropdownYield
        @dropdownClass=".dropdown-target"
        {{! @glint-expect-error: Incorrect type }}
        @dropdownClassName="dropdown-target right-0"
        @onCancel={{this.onClose}}
        @selected={{this.triggerLabel}}
        @triggerComponent={{GeneralDropdownsTriggersTriggerDropdownBasic}}
        as |dropdown|
      >
        <div
          class="flex flex-col {{styles.picker}}"
          data-test-id="profile-currency-picker"
        >
          {{! Header: Search }}
          <div class="border-neutral-default border-b p-200">
            <div class="flex">
              <PrismInput
                @autofocus={{true}}
                @iconAfter={{hash name="search"}}
                @onInput={{pick "target.value" this.onSearchInputTask.perform}}
                @placeholder={{t "general.search_currency"}}
                @removePadding={{true}}
                @value={{this.searchValue}}
                data-test-id="profile-currency-picker-search"
              />
            </div>
          </div>

          {{! Body: Currency groups }}
          <div class="flex-1 overflow-y-auto">
            {{#if this.favouriteOptions.length}}
              <GeneralCurrencyPickerGroup
                @label={{t "general.favourites"}}
                data-test-id="profile-currency-picker-group-favourites"
              >
                {{#each this.favouriteOptions as |option|}}
                  <GeneralCurrencyPickerItem
                    @dropdown={{dropdown}}
                    @onClick={{fn this.selectCurrency option}}
                    class={{if
                      (eq option.value @selectedCurrencyCode)
                      "bg-ghost-selected"
                    }}
                    data-test-id="profile-currency-picker-item"
                  >
                    <:default>
                      <span class="truncate" title={{option.label}}>
                        {{option.label}}
                      </span>
                    </:default>
                    <:actions>
                      <div class="flex items-center">
                        <PrismButton
                          @buttonType="ghost"
                          @icon="star-filled"
                          @onClick={{stopPropagation
                            (fn
                              this.toggleFavouriteTask.perform option.currency
                            )
                          }}
                          class={{local
                            styles
                            "favourite-button"
                            "favourite-button--active"
                          }}
                          data-test-id="profile-currency-picker-favourite-button"
                        />
                        {{#if (eq option.value @selectedCurrencyCode)}}
                          <PrismIcon @name="checkmark" @size="small" />
                        {{/if}}
                      </div>
                    </:actions>
                  </GeneralCurrencyPickerItem>
                {{/each}}
              </GeneralCurrencyPickerGroup>
            {{/if}}

            <GeneralCurrencyPickerGroup
              @label={{t "general.all"}}
              data-test-id="profile-currency-picker-group-all"
            >
              {{#each this.filteredOptions as |option|}}
                <GeneralCurrencyPickerItem
                  @dropdown={{dropdown}}
                  @onClick={{fn this.selectCurrency option}}
                  class={{if
                    (eq option.value @selectedCurrencyCode)
                    "bg-ghost-selected"
                  }}
                  data-test-id="profile-currency-picker-item"
                >
                  <:default>
                    <span class="truncate" title={{option.label}}>
                      {{option.label}}
                    </span>
                  </:default>
                  <:actions>
                    <div class="flex items-center">
                      <PrismButton
                        @buttonType="ghost"
                        @icon={{if
                          option.currency.isFavourite
                          "star-filled"
                          "star-outline"
                        }}
                        @onClick={{stopPropagation
                          (fn this.toggleFavouriteTask.perform option.currency)
                        }}
                        class={{local
                          styles
                          "favourite-button"
                          (if
                            option.currency.isFavourite
                            "favourite-button--active"
                          )
                        }}
                        data-test-id="profile-currency-picker-favourite-button"
                      />
                      {{#if (eq option.value @selectedCurrencyCode)}}
                        <PrismIcon @name="checkmark" @size="small" />
                      {{/if}}
                    </div>
                  </:actions>
                </GeneralCurrencyPickerItem>
              {{else}}
                <div
                  class="text-300--regular flex min-h-1000 items-center px-300"
                >
                  {{t "general.no_results_found"}}
                </div>
              {{/each}}
            </GeneralCurrencyPickerGroup>
          </div>

          {{! Footer: View profile currencies + Reset }}
          <div class="border-neutral-emphasis flex gap-200 border-t p-200">
            <PrismButton
              @buttonType="secondary"
              @iconBefore="visibility-on"
              @label={{t
                "currency_conversion.profile_currency_picker.view_profile_currencies"
              }}
              @onClick={{fn this.openProfileCurrenciesModal dropdown}}
              class="flex-1"
              data-test-id="profile-currency-picker-view-profile-currencies"
            />

            <div class="flex">
              <PrismButton
                @buttonType="secondary"
                @iconBefore="reset-currency"
                @isDisabled={{this.isResetDisabled}}
                @label={{t "general.reset"}}
                @onClick={{fn this.resetCurrency dropdown}}
                data-test-id="profile-currency-picker-reset"
              />
              {{#if this.isResetDisabled}}
                <PrismTooltip
                  @text={{t
                    "currency_conversion.profile_currency_picker.reset_tooltip"
                  }}
                />
              {{/if}}
            </div>
          </div>
        </div>
      </GeneralDropdownsDropdownYield>
    </div>

    {{#if this.isProfileCurrenciesModalOpen}}
      {{#if @selectedProfile}}
        <HotelAndMarketCurrencyProfileCurrenciesModal
          @onClose={{this.closeProfileCurrenciesModal}}
          @profile={{@selectedProfile}}
        />
      {{/if}}
    {{/if}}
  </template>
}
